import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { LoanService } from '@ld.consumer-portal/data-access';
import {
  ConsentStorageKeys,
  getEncryptionValueForLoan,
  Loan,
  LoanFlag,
  LoanResponse,
  TcpaConsentLoan,
  UserProfile,
} from '@ld.consumer-portal/models';
import { LoggingService } from '@ld.consumer-portal/utils';
import { MODAL_IDS, MODAL_PRIORITIES, ModalQueueService } from './modal-queue.service';

@Injectable({
  providedIn: 'root',
})
export class ConsentNavigationService {
  private readonly router = inject(Router);
  private readonly loanService = inject(LoanService);
  private readonly loggingService = inject(LoggingService);
  private readonly modalQueueService = inject(ModalQueueService);

  private loanFlags: LoanFlag[] = [];

  async getPostLoginRoute(loanResponses: LoanResponse[], userProfile: UserProfile): Promise<string> {
    if (!loanResponses.length) {
      return '/dashboard';
    }

    await this.loadLoanFlags(loanResponses, userProfile);

    const firstLoan = loanResponses[0].loan;

    if (this.isAutopayConsentRequired(firstLoan.loanNumber)) {
      return '/autopay-consent';
    }

    if (this.isPaperlessPromoRequired(firstLoan)) {
      return '/paperless';
    }

    return '/dashboard';
  }

  async navigateAfterLogin(loanResponses: LoanResponse[], userProfile: UserProfile): Promise<void> {
    const route = await this.getPostLoginRoute(loanResponses, userProfile);
    this.router.navigate([route]);
  }

  private async loadLoanFlags(loanResponses: LoanResponse[], userProfile: UserProfile): Promise<void> {
    const loanRequests = loanResponses.map((response) => ({
      loanNumber: response.loan.loanNumber,
      encryptionValue: getEncryptionValueForLoan(userProfile, response.loan.loanNumber) || '',
    }));

    try {
      this.loanFlags = await this.loanService.getLoanFlags(loanRequests);
    } catch (error) {
      this.loggingService.logException(error, 'Error loading loan flags:');
      this.loanFlags = [];
    }
  }

  isAutopayConsentRequired(loanNumber: string): boolean {
    return this.loanFlags.some((f) => f.loanNumber === loanNumber && f.name === 'AutopayEConsent');
  }

  isTcpaConsentRequired(loanResponses: LoanResponse[], userProfile: UserProfile): boolean {
    if (localStorage.getItem(ConsentStorageKeys.TcpaConsentCompleted)) {
      return false;
    }

    return loanResponses.some((response) => {
      const loan = response.loan;
      const isTcpaFlagEnabled = this.loanFlags.some((f) => f.loanNumber === loan.loanNumber && f.name === 'TCPA');
      return isTcpaFlagEnabled && this.isLoanTcpaConsentDue(loan, userProfile);
    });
  }

  private isLoanTcpaConsentDue(loan: LoanResponse['loan'], userProfile: UserProfile): boolean {
    const userSsn = this.getUserSsnFromProfile(userProfile, loan.loanNumber);

    if (!userSsn) {
      return false;
    }

    const isBorrowerConsentDue = loan.isBorrowerTcpaConsentDue && loan.borrower?.ssn === userSsn;

    const isCoBorrowerConsentDue = loan.isCoborrowerTcpaConsentDue && loan.coBorrower?.ssn === userSsn;

    return isBorrowerConsentDue || isCoBorrowerConsentDue;
  }

  getLoansRequiringTcpaConsent(loanResponses: LoanResponse[], userProfile: UserProfile): TcpaConsentLoan[] {
    return loanResponses
      .filter((response) => this.isLoanTcpaConsentDue(response.loan, userProfile))
      .map((response) => {
        const loan = response.loan;
        const encryptionValue = getEncryptionValueForLoan(userProfile, loan.loanNumber) || '';

        return {
          loanNumber: loan.loanNumber,
          encryptionValue,
          isBorrowerTcpaConsentDue: loan.isBorrowerTcpaConsentDue,
          isCoborrowerTcpaConsentDue: loan.isCoborrowerTcpaConsentDue,
          borrowerId: loan.borrower?.id || '',
          borrowerSsn: loan.borrower?.ssn || '',
          coBorrowerId: loan.coBorrower?.id,
          coBorrowerSsn: loan.coBorrower?.ssn,
        };
      });
  }

  isPaperlessPromoRequired(loan: LoanResponse['loan']): boolean {
    if (localStorage.getItem(ConsentStorageKeys.PaperlessPromoDismissed)) {
      return false;
    }

    return loan.showPaperLessPromo ?? false;
  }

  private getUserSsnFromProfile(userProfile: UserProfile, loanNumber: string): string | null {
    if (!userProfile.properties) {
      return null;
    }

    const loanProperty = userProfile.properties.find((p) => p.key === 'LoanNumber' && p.value === loanNumber);

    if (!loanProperty) {
      return null;
    }

    const ssnProperty = userProfile.properties.find(
      (p) => p.key === 'LastSSN' && p.attributeCollectionId === loanProperty.attributeCollectionId,
    );

    return ssnProperty?.value || null;
  }

  markTcpaConsentCompleted(): void {
    localStorage.setItem(ConsentStorageKeys.TcpaConsentCompleted, 'true');
  }

  markAutopayConsentCompleted(): void {
    localStorage.setItem(ConsentStorageKeys.AutopayConsentCompleted, 'true');
  }

  clearAutopayConsentMarker(): void {
    localStorage.removeItem(ConsentStorageKeys.AutopayConsentCompleted);
  }

  markPaperlessPromoDismissed(): void {
    localStorage.setItem(ConsentStorageKeys.PaperlessPromoDismissed, 'true');
  }

  clearConsentMarkers(): void {
    localStorage.removeItem(ConsentStorageKeys.TcpaConsentCompleted);
    localStorage.removeItem(ConsentStorageKeys.AutopayConsentCompleted);
    // Note: Paperless promo dismissal is persistent
    // Note: Contact info review shown is persistent
  }

  readonly showTcpaConsentModal = signal(false);

  openTcpaConsentModal(): void {
    this.showTcpaConsentModal.set(true);
  }

  closeTcpaConsentModal(): void {
    this.showTcpaConsentModal.set(false);
  }

  triggerTcpaConsentIfNeeded(loanResponses: LoanResponse[], userProfile: UserProfile): void {
    if (this.isTcpaConsentRequired(loanResponses, userProfile)) {
      this.showTcpaConsentModal.set(true);
    }
  }

  readonly showContactInfoReviewModal = computed(() => this.modalQueueService.activeModal() === MODAL_IDS.CONTACT_INFO_REVIEW);

  isContactInfoReviewRequired(userProfile: UserProfile, loanResponses: LoanResponse[]): boolean {
    if (localStorage.getItem(ConsentStorageKeys.ContactInfoReviewShown)) {
      return false;
    }

    if (!userProfile.showContactReview) {
      return false;
    }

    const hasEligibleLoan = loanResponses.some((response) => this.isLoanEligibleForContactReview(response.loan));

    return hasEligibleLoan;
  }

  private isLoanEligibleForContactReview(loan: Loan): boolean {
    return (
      !loan.isTransferredToCenlar &&
      !loan.isTransferredToThirdParty &&
      !loan.isPIITServiceTransfer &&
      !loan.isEarlyTransferToCenlar
    );
  }

  triggerContactInfoReviewIfNeeded(userProfile: UserProfile, loanResponses: LoanResponse[]): void {
    if (this.isContactInfoReviewRequired(userProfile, loanResponses)) {
      this.modalQueueService.enqueue(MODAL_IDS.CONTACT_INFO_REVIEW, MODAL_PRIORITIES[MODAL_IDS.CONTACT_INFO_REVIEW]);
    }
  }

  closeContactInfoReviewModal(): void {
    this.modalQueueService.dismiss(MODAL_IDS.CONTACT_INFO_REVIEW);
  }

  markContactInfoReviewShown(): void {
    localStorage.setItem(ConsentStorageKeys.ContactInfoReviewShown, 'true');
  }

  private readonly _updatedPhoneNumbers = signal<Map<string, string[]>>(new Map());
  readonly updatedPhoneNumbers = this._updatedPhoneNumbers.asReadonly();

  setUpdatedPhoneNumbers(loanNumber: string, phoneNumbers: string[]): void {
    this._updatedPhoneNumbers.update((map) => {
      const newMap = new Map(map);
      newMap.set(loanNumber, phoneNumbers);
      return newMap;
    });

    localStorage.setItem(`${ConsentStorageKeys.PhoneNumberUpdated}_${loanNumber}`, 'true');
  }

  getUpdatedPhoneNumbers(loanNumber: string): string[] {
    return this._updatedPhoneNumbers().get(loanNumber) || [];
  }

  hasUpdatedPhoneNumbers(loanNumber: string): boolean {
    return localStorage.getItem(`${ConsentStorageKeys.PhoneNumberUpdated}_${loanNumber}`) === 'true';
  }

  clearUpdatedPhoneNumbers(loanNumber: string): void {
    this._updatedPhoneNumbers.update((map) => {
      const newMap = new Map(map);
      newMap.delete(loanNumber);
      return newMap;
    });
    localStorage.removeItem(`${ConsentStorageKeys.PhoneNumberUpdated}_${loanNumber}`);
  }

  clearAllUpdatedPhoneNumbers(): void {
    this._updatedPhoneNumbers.set(new Map());
  }
}
